// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useRef, useState } from 'react';
import { AppIcon } from '@loa/ui-components';

export type ToolKey =
  | 'investment-fund'
  | 'max-funding'
  | 'suitability'
  | 'policy-schedule';

interface ToolItem {
  key: ToolKey;
  label: string;
  enabled: boolean;
}

// Cashflow modelling graduated out of Tools to the first-class Cashflow tab
// (docs/specs/cashflow-modelling-tab-prd.md R5) — no menu entry here.
const TOOLS: ToolItem[] = [
  { key: 'investment-fund', label: 'Investment fund comparison', enabled: false },
  { key: 'max-funding', label: 'Max funding calculator', enabled: true },
  { key: 'suitability', label: 'Suitability statements', enabled: false },
  { key: 'policy-schedule', label: 'Policy schedule overview', enabled: false },
];

type ToolsMenuProps = Readonly<{
  onSelectTool: (key: ToolKey) => void;
  className?: string;
  variant?: 'tab' | 'button';
}>;

export function ToolsMenu({ onSelectTool, className = '', variant = 'tab' }: ToolsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (tool: ToolItem) => {
    if (!tool.enabled) return;
    setOpen(false);
    onSelectTool(tool.key);
  };

  const tabOpenColor = open ? 'text-purple-primary' : 'text-grey-04 hover:text-purple-primary';
  const triggerClasses =
    variant === 'tab'
      ? `flex items-center gap-2 bg-none border-none px-md py-sm font-sans text-body-02 font-normal leading-normal cursor-pointer transition-colors ${tabOpenColor}`
      : 'cursor-pointer flex items-center justify-center h-[40px] px-md gap-sm rounded-lg border border-purple-primary bg-transparent hover:bg-lilac-06 transition-colors';

  const triggerLabelClasses =
    variant === 'tab'
      ? ''
      : "text-purple-primary font-sans text-body-02 font-medium leading-normal";

  let chevronColor: string;
  if (variant === 'tab') chevronColor = open ? '#2D0A6C' : '#525252';
  else chevronColor = '#2D0A6C';

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={triggerClasses}
        aria-label="Tools"
        data-testid="tools-menu-trigger"
      >
        <span className={triggerLabelClasses}>Tools</span>
        <AppIcon name={open ? 'chevron up' : 'chevron down'} size={16} color={chevronColor} />
      </button>

      {open && (
        <div
          className="absolute top-[calc(100%+8px)] right-0 w-[288px] inline-flex p-md flex-col items-start gap-sm rounded-lg bg-white z-50"
          style={{ boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.10)' }}
          data-testid="tools-menu-dropdown"
        >
          {TOOLS.map((tool) => {
            let toolClass = 'cursor-not-allowed text-[#9B9B9B]';
            if (tool.enabled) toolClass = 'cursor-pointer hover:bg-[#F5F5F7] text-black';
            return (
              <button
                key={tool.key}
                type="button"
                className={`flex items-center gap-sm px-md py-sm self-stretch rounded-lg transition-colors bg-transparent border-0 text-left ${toolClass}`}
                onClick={() => handleSelect(tool)}
                data-testid={`tools-menu-item-${tool.key}`}
                disabled={!tool.enabled}
                aria-disabled={!tool.enabled}
              >
                <span className="font-sans text-body-02 font-normal leading-normal">
                  {tool.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
